import * as Sentry from "@sentry/react";

import { AuthedRoute, PrivateRoute } from "containers/PrivateRoute";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import {
  SuspenseApOrder,
  SuspenseOrder,
  SuspenseProgramSelector,
  SuspenseViewCampaign,
  SuspenseChangedPasswordSuccess,
  SuspenseLogOut,
  SuspenseReports,
  SuspenseResetPassword,
  SuspenseResults,
  SuspenseSetPassword,
  SuspenseSoldPrice,
  SuspenseTemporaryPassword,
  SuspenseTokenExpired,
  SuspenseUnauthorized,
  SuspenseOrderCampaign,
  SuspenseSellersChoice,
  SuspenseReporting,
  SuspenseEmail
} from "routes/loadables";

import AlertMessage from "components/Alert";
import AppRoot from "containers/AppRoot";
import CookiesBar from "components/CookiesConsentBar";
import GlobalStyle from "style/globalStyle";
import { HandleSelectedHqHOC } from "components/HandleSelectedHqHoc";
import Login from "routes/auth/Login";
import SetupAccount from "routes/auth/SetupAccount";
import { ProgramBuilderDetails } from "./back-office/program-builder/details";
import { ProgramBuilderOverview } from "./back-office/program-builder/overview";
import { PropertyList } from "routes/customer/property";
import { RefreshCredentialsHOC } from "components/RefreshCredentialsHOC";
import TemporaryError from "./auth/TemporaryError";
import UserTracking from "components/UserTracking/UserTracking";
import ChannelIntegration from "./channel-integration/channel-integration";
import { HandleCustomerHqAndOfficeHoc } from "components/HandleCustomerHqAndOfficeHoc";
import { BillingPage } from "./customer/billing";
import { V3Order } from "./legacy-order-pages/V3Order";
import { TriggerPage } from "./customer/triggers";
import { SuspenseBackoffice } from "./loadables";
import { IntegrationPage } from "./customer/integrations";
import { CampaignsPage } from "./customer/campaigns";
import { AgentProfile } from "./customer/agent-profile";
import { SelectRoleDropdown } from "components/SelectRoleDropdown";
import { HqPathUpdater } from "components/HqPathUpdater/HqPathUpdater";
import { LegacyResult } from "./property/routes/results/LegacyResult";
import { PublicDomainPage } from "./public/PublicDomainPage";
import { InviteSellerFromEmail } from "./customer/property/InviteSellerFromEmail";

export const AppRoutes = () => {
  return (
    <BrowserRouter>
      <CookiesBar />
      <AlertMessage />
      <SelectRoleDropdown />

      <AppRoot>
        <Sentry.ErrorBoundary>
          {process.env.JEST_WORKER_ID === undefined &&
            process.env.NODE_ENV !== "development" && <UserTracking />}
          <GlobalStyle />
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route
              path="/email-domain-authentication/:domainId"
              element={<PublicDomainPage />}
            />{" "}
            {/* Logout must be outside the AuthencationHOC to avoid timing issues with logging out/the auth hoc */}
            <Route path="/auth/logout" element={<SuspenseLogOut />} />
            <Route path="/password/reset" element={<SuspenseResetPassword />} />
            <Route path="/password" element={<SuspenseSetPassword />} />
            <Route
              path="/password-changed/success"
              element={<SuspenseChangedPasswordSuccess />}
            />
            <Route
              path="/password/temporary"
              element={<SuspenseTemporaryPassword />}
            />
            <Route
              path="/error/expired-token"
              element={<SuspenseTokenExpired />}
            />
            <Route path="/setup-account" element={<SetupAccount />} />
            <Route
              path="/login"
              element={
                <RefreshCredentialsHOC>
                  <Login />
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/error/unauthorized"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <SuspenseUnauthorized />
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/error/temporary-error"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <TemporaryError />
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/view/:propertyId"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <PrivateRoute action="View" scope="Campaign">
                      <SuspenseViewCampaign />
                    </PrivateRoute>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            {/* Legacy route that stays because old emails lead here */}
            <Route
              path="v3/ap-order"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <PrivateRoute action="View" scope="Campaign">
                      <V3Order pageMode="ap" />
                    </PrivateRoute>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            {/* Legacy route that stays because old emails lead here */}
            <Route
              path="v3/order/:step"
              element={
                <RefreshCredentialsHOC>
                  <PrivateRoute action="View" scope="Campaign">
                    <V3Order pageMode="customer" />
                  </PrivateRoute>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="customer/hq/:hqId/order/:step"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <PrivateRoute action="View" scope="Campaign">
                      <SuspenseOrder />
                    </PrivateRoute>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/hq/:hqId/ap-order"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <PrivateRoute action="View" scope="Campaign">
                      <SuspenseApOrder />
                    </PrivateRoute>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/program-selector/:propertyId"
              element={
                <RefreshCredentialsHOC>
                  {/* TODO Exchange HandleSelectedHQHOC to HOC to get user data */}
                  <HandleSelectedHqHOC>
                    <PrivateRoute action="View" scope="Campaign">
                      <SuspenseProgramSelector />
                    </PrivateRoute>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/analytics"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <PrivateRoute scope="Reports" action="View">
                      <SuspenseReports />
                    </PrivateRoute>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            {/* Legacy route */}
            <Route
              path="/property/list"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <PrivateRoute scope="Listing" action="View">
                      <PropertyList />
                    </PrivateRoute>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/property/:propertyId/invite-seller"
              element={
                <RefreshCredentialsHOC>
                  <HqPathUpdater>
                    <PrivateRoute action="View" scope="Listing">
                      <InviteSellerFromEmail />
                    </PrivateRoute>
                  </HqPathUpdater>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/property"
              element={
                <RefreshCredentialsHOC>
                  <HqPathUpdater>
                    <PrivateRoute scope="Listing" action="View">
                      <PropertyList />
                    </PrivateRoute>
                  </HqPathUpdater>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/property/results"
              element={
                <RefreshCredentialsHOC>
                  <AuthedRoute>
                    <LegacyResult />
                  </AuthedRoute>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/property/results/:guid"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <AuthedRoute>
                      <SuspenseResults />
                    </AuthedRoute>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/v2/property/results"
              element={
                <Navigate
                  to={{
                    pathname: "/property/results",
                    search: window.location.search
                  }}
                  replace
                />
              }
            />
            <Route
              path="/property/statistics"
              element={<Navigate to="/property/results" replace />}
            />
            <Route
              path="/soldprice"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <AuthedRoute>
                      <SuspenseSoldPrice />
                    </AuthedRoute>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/program-builder"
              element={
                <RefreshCredentialsHOC>
                  <HqPathUpdater>
                    <PrivateRoute scope="Program" action="View">
                      <ProgramBuilderOverview appMode={"customer"} />
                    </PrivateRoute>
                  </HqPathUpdater>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/program-builder/:programId"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <PrivateRoute scope="Program" action="View">
                      <ProgramBuilderDetails appMode={"customer"} />
                    </PrivateRoute>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            {/* BO ROUTES START HERE */}
            <Route path="/back-office/*" element={<SuspenseBackoffice />} />
            <Route
              path="/customer/hq/:hqId/facebook/integration"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <HandleCustomerHqAndOfficeHoc>
                      <AuthedRoute>
                        <ChannelIntegration />
                      </AuthedRoute>
                    </HandleCustomerHqAndOfficeHoc>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/office/:officeId/facebook/integration"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <HandleCustomerHqAndOfficeHoc>
                      <AuthedRoute>
                        <ChannelIntegration />
                      </AuthedRoute>
                    </HandleCustomerHqAndOfficeHoc>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/billing"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <HandleCustomerHqAndOfficeHoc>
                      <AuthedRoute>
                        <BillingPage />
                      </AuthedRoute>
                    </HandleCustomerHqAndOfficeHoc>
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/triggers"
              element={
                <RefreshCredentialsHOC>
                  <HqPathUpdater>
                    <PrivateRoute scope="Program" action="View">
                      <TriggerPage />
                    </PrivateRoute>
                  </HqPathUpdater>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/campaigns"
              element={
                <RefreshCredentialsHOC>
                  <HqPathUpdater>
                    <PrivateRoute scope="Campaign" action="View">
                      <CampaignsPage />
                    </PrivateRoute>
                  </HqPathUpdater>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/order-campaign"
              element={
                <RefreshCredentialsHOC>
                  <HqPathUpdater>
                    <SuspenseOrderCampaign />
                  </HqPathUpdater>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/integrations"
              element={
                <RefreshCredentialsHOC>
                  <HqPathUpdater>
                    <AuthedRoute>
                      <IntegrationPage />
                    </AuthedRoute>
                  </HqPathUpdater>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/hq/:hqId/agent-profile/:agentId"
              element={
                <RefreshCredentialsHOC>
                  <HandleSelectedHqHOC>
                    <AgentProfile />
                  </HandleSelectedHqHOC>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/sellers-choice-order-page/*"
              element={
                <RefreshCredentialsHOC>
                  <PrivateRoute action="Order" scope="Campaign">
                    <SuspenseSellersChoice />
                  </PrivateRoute>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/reporting/*"
              element={
                <RefreshCredentialsHOC>
                  <HqPathUpdater>
                    <PrivateRoute action="View" scope="Program">
                      <SuspenseReporting />
                    </PrivateRoute>
                  </HqPathUpdater>
                </RefreshCredentialsHOC>
              }
            />
            <Route
              path="/customer/hq/:hqId/email"
              element={
                <RefreshCredentialsHOC>
                  <HqPathUpdater>
                    <PrivateRoute action="View" scope="SelfServe">
                      <SuspenseEmail />
                    </PrivateRoute>
                  </HqPathUpdater>
                </RefreshCredentialsHOC>
              }
            />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Sentry.ErrorBoundary>
      </AppRoot>
    </BrowserRouter>
  );
};
